import React, { useEffect, useState } from 'react';
import { X, Coins, Clock, Star, Pencil } from 'lucide-react';

// ──────────────────────────────────────────────────────────────────────────
// Bottom sheet for hiring (or extending) the Restorer.
// Header now shows a portrait + editable nickname (default "Jeff").
// Shift cards are uniformly styled in the game's gold palette; the
// premium tier keeps a richer glow + gold Stars price for distinction.
// ──────────────────────────────────────────────────────────────────────────

const SHIFTS = [
  {
    key: 'hour',
    label: { en: '1 hour', ru: '1 час' },
    sublabel: { en: 'quick fix', ru: 'быстрый ремонт' },
    cost: 300, currency: 'coins',
  },
  {
    key: 'halfday',
    label: { en: '12 hours', ru: '12 часов' },
    sublabel: { en: 'half-day shift', ru: 'половина дня' },
    cost: 3000, currency: 'coins',
  },
  {
    key: 'day',
    label: { en: '24 hours', ru: '24 часа' },
    sublabel: { en: 'full day · best value', ru: 'сутки · выгодно' },
    cost: 5000, currency: 'coins',
    badge: { en: 'BEST', ru: 'ХИТ' },
  },
  {
    key: 'month',
    label: { en: '1 month', ru: '1 месяц' },
    sublabel: { en: '30 days non-stop · premium', ru: '30 дней без остановки · премиум' },
    cost: 100, currency: 'stars',
    premium: true,
    badge: { en: 'PREMIUM', ru: 'ПРЕМИУМ' },
  },
];

const fmt = (n) => n.toLocaleString();

// Larger version of the team-slot portrait. Identical character traits
// so the player recognises Jeff at every scale: the team panel, the
// hire sheet header, future settings page.
const Portrait = ({ size = 56 }) => (
  <svg
    viewBox="0 0 64 64"
    width={size}
    height={size}
    aria-hidden
    style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))' }}
  >
    <defs>
      <radialGradient id="hp-skin" cx="50%" cy="42%" r="55%">
        <stop offset="0%" stopColor="#F5DCBB" />
        <stop offset="100%" stopColor="#D9B388" />
      </radialGradient>
      <linearGradient id="hp-coat" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#3A5A8A" />
        <stop offset="100%" stopColor="#1F3358" />
      </linearGradient>
      <linearGradient id="hp-hat" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#1A1A26" />
        <stop offset="100%" stopColor="#0A0A12" />
      </linearGradient>
      <linearGradient id="hp-bow" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#F5D07A" />
        <stop offset="100%" stopColor="#B8862A" />
      </linearGradient>
    </defs>
    <path d="M 8 64 C 8 48, 22 42, 32 42 C 42 42, 56 48, 56 64 Z" fill="url(#hp-coat)" />
    <path d="M 16 60 C 22 54, 28 50, 32 49 C 36 50, 42 54, 48 60" fill="none" stroke="#15233F" strokeWidth="1" strokeLinecap="round" />
    <path d="M 26 49 L 32 46 L 38 49 L 36 53 L 32 51 L 28 53 Z" fill="url(#hp-bow)" stroke="#7A5A1A" strokeWidth="0.5" />
    <circle cx="32" cy="49.5" r="1" fill="#5A3F0F" />
    <path d="M 28 42 L 28 47 L 36 47 L 36 42 Z" fill="#C9A075" />
    <ellipse cx="32" cy="32" rx="11" ry="13" fill="url(#hp-skin)" />
    <ellipse cx="25" cy="35" rx="2.5" ry="1.6" fill="#E2A887" opacity="0.55" />
    <ellipse cx="39" cy="35" rx="2.5" ry="1.6" fill="#E2A887" opacity="0.55" />
    <ellipse cx="27.2" cy="30" rx="1.1" ry="1.4" fill="#1B1B26" />
    <ellipse cx="36.8" cy="30" rx="1.1" ry="1.4" fill="#1B1B26" />
    <circle cx="27.5" cy="29.5" r="0.4" fill="#FFFFFF" opacity="0.85" />
    <circle cx="37.1" cy="29.5" r="0.4" fill="#FFFFFF" opacity="0.85" />
    <path d="M 24.5 27 Q 27 26 29.5 27" stroke="#3D2A1A" strokeWidth="1.1" fill="none" strokeLinecap="round" />
    <path d="M 34.5 27 Q 37 26 39.5 27" stroke="#3D2A1A" strokeWidth="1.1" fill="none" strokeLinecap="round" />
    <path d="M 23 37.4 Q 27 36.2 31 37.4 Q 32 38.4 31.5 38.6 Q 27.5 38.2 23 38.6 Q 22 38.4 23 37.4 Z" fill="#3D2A1A" />
    <path d="M 41 37.4 Q 37 36.2 33 37.4 Q 32 38.4 32.5 38.6 Q 36.5 38.2 41 38.6 Q 42 38.4 41 37.4 Z" fill="#3D2A1A" />
    <path d="M 30 41 Q 32 41.6 34 41" stroke="#7A4A2A" strokeWidth="0.7" fill="none" strokeLinecap="round" />
    <rect x="20" y="10" width="24" height="14" rx="1.5" fill="url(#hp-hat)" />
    <rect x="20.5" y="11" width="1" height="12" fill="#2A2A38" opacity="0.7" />
    <rect x="20" y="20" width="24" height="2.2" fill="url(#hp-bow)" />
    <ellipse cx="32" cy="24.5" rx="17" ry="2.6" fill="url(#hp-hat)" />
    <path d="M 18 24.5 Q 32 22.7 46 24.5" stroke="#3A3A48" strokeWidth="0.6" fill="none" opacity="0.6" />
  </svg>
);

const RestorerHireSheet = ({
  open,
  language = 'en',
  coins = 0,
  hiredUntil,
  restorerName = 'Jeff',
  onRequestRename,
  onConfirm,
  onClose,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [shouldRender, setShouldRender] = useState(open);
  const [kbOffset, setKbOffset] = useState(0);

  useEffect(() => {
    if (open) { setShouldRender(true); return; }
    if (!shouldRender) return;
    const id = setTimeout(() => setShouldRender(false), 220);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!shouldRender) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [shouldRender]);

  // Keep visualViewport tracking even though we no longer host the
  // rename input here — future inputs (e.g. tip, message) might land
  // in this sheet, so the lift-above-keyboard behaviour is cheap to
  // keep around.
  useEffect(() => {
    if (!shouldRender) return;
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!vv) return;
    const sync = () => {
      const offset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      setKbOffset(offset);
    };
    sync();
    vv.addEventListener('resize', sync);
    vv.addEventListener('scroll', sync);
    return () => {
      vv.removeEventListener('resize', sync);
      vv.removeEventListener('scroll', sync);
      setKbOffset(0);
    };
  }, [shouldRender]);

  if (!shouldRender) return null;

  const exiting = !open;
  const isExtend = !!(hiredUntil && new Date(hiredUntil) > new Date());

  const close = () => {
    if (submitting) return;
    onClose && onClose();
  };

  const requestRename = () => {
    if (submitting) return;
    onRequestRename && onRequestRename();
  };

  const handlePick = async (shift) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await onConfirm(shift);
      close();
    } finally {
      setSubmitting(false);
    }
  };

  const remaining = (() => {
    if (!hiredUntil) return null;
    const ms = new Date(hiredUntil).getTime() - Date.now();
    if (ms <= 0) return null;
    const totalSec = Math.floor(ms / 1000);
    const d = Math.floor(totalSec / 86400);
    const h = Math.floor((totalSec % 86400) / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    if (d > 0) return language === 'ru' ? `${d}д ${h}ч` : `${d}d ${h}h`;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  })();

  return (
    <div
      data-testid="restorer-hire-sheet"
      className={`fixed inset-0 z-[60] flex items-end sm:items-center justify-center ${
        exiting ? 'animate-out fade-out duration-200' : 'animate-in fade-in duration-200'
      }`}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-[2px]" onClick={close} />
      <div
        className={`relative w-full sm:max-w-sm bg-[#0E0E18] border border-[#D4AF37]/20 rounded-t-2xl sm:rounded-2xl shadow-[0_-8px_30px_rgba(0,0,0,0.6)] px-4 pt-3 pb-5 ${
          exiting ? 'animate-out slide-out-to-bottom duration-200' : 'animate-in slide-in-from-bottom duration-300'
        }`}
        style={{
          paddingBottom: 'max(20px, env(safe-area-inset-bottom))',
          transform: !exiting && kbOffset > 0 ? `translateY(-${kbOffset}px)` : undefined,
          transition: 'transform 180ms ease-out',
        }}
      >
        <div className="mx-auto w-10 h-1 rounded-full bg-[#3F3F46]/70 mb-3" />

        {/* Header: portrait + editable nickname + role */}
        <div className="flex items-start gap-3 mb-3">
          <div className="relative w-14 h-14 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/40 flex items-center justify-center overflow-hidden shrink-0">
            <div style={{ transform: 'translateY(-2px)' }}>
              <Portrait size={48} />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <button
              data-testid="restorer-name-edit"
              onClick={requestRename}
              className="group inline-flex items-center gap-1.5 text-left"
            >
              <span className="text-[18px] font-semibold text-white leading-tight">
                {restorerName || 'Jeff'}
              </span>
              <Pencil className="w-3 h-3 text-[#A1A1AA]/60 group-hover:text-[#D4AF37] transition-colors" />
            </button>
            <div className="text-[10px] text-[#A1A1AA]/70 uppercase tracking-[0.18em] mt-0.5">
              {language === 'ru' ? 'Реставратор · команда' : 'Restorer · team'}
            </div>
            <div className="text-[12px] text-[#A1A1AA] mt-1">
              {isExtend
                ? (language === 'ru' ? 'Продлить смену' : 'Extend shift')
                : (language === 'ru' ? 'Выберите длительность' : 'Pick a shift')}
            </div>
          </div>
          <button
            data-testid="hire-sheet-close"
            onClick={close}
            className="p-1 rounded-full hover:bg-white/5 transition-colors"
          >
            <X className="w-4 h-4 text-[#A1A1AA]" />
          </button>
        </div>

        {isExtend && remaining && (
          <div className="mb-3 px-3 py-2 rounded-xl bg-[#10B981]/10 border border-[#10B981]/30 flex items-center gap-2">
            <Clock className="w-3.5 h-3.5 text-[#34D399]" />
            <span className="text-[12px] text-[#34D399]">
              {language === 'ru' ? `Работает ещё: ${remaining}` : `Working for: ${remaining}`}
            </span>
          </div>
        )}

        <div className="mb-3 flex items-center justify-between text-[12px] text-[#A1A1AA]">
          <span>{language === 'ru' ? 'Ваш баланс' : 'Your balance'}</span>
          <span className="flex items-center gap-1 font-mono text-white tabular-nums">
            <Coins className="w-3.5 h-3.5 text-[#D4AF37]" />
            {fmt(coins)}
          </span>
        </div>

        {/* Unified shift cards — all in the game's gold palette. Premium
            tier still gets a slightly stronger glow + gold Stars to keep
            it distinct, but the colour family stays consistent. */}
        <div className="space-y-2">
          {SHIFTS.map((s) => {
            const isStars = s.currency === 'stars';
            const affordable = isStars ? true : coins >= s.cost;
            const disabled = !affordable || submitting;

            const baseCls = !affordable
              ? 'border-[#3F3F46]/40 bg-[#141420] opacity-60 cursor-not-allowed'
              : s.premium
                ? 'border-[#D4AF37]/60 bg-gradient-to-r from-[#D4AF37]/15 via-[#D4AF37]/8 to-transparent shadow-[0_0_18px_rgba(212,175,55,0.22)] hover:border-[#D4AF37]/80 hover:shadow-[0_0_24px_rgba(212,175,55,0.32)] active:scale-[0.985]'
                : 'border-[#D4AF37]/35 bg-gradient-to-r from-[#D4AF37]/10 to-transparent hover:border-[#D4AF37]/65 hover:from-[#D4AF37]/15 active:scale-[0.985]';

            return (
              <button
                key={s.key}
                data-testid={`hire-shift-${s.key}`}
                disabled={disabled}
                onClick={() => handlePick(s.key)}
                className={`w-full text-left rounded-2xl px-4 py-3 border transition-all relative overflow-hidden ${baseCls} ${
                  s.premium ? 'border-2' : ''
                }`}
              >
                {s.badge && affordable && (
                  <span
                    className={`absolute -right-7 top-2 rotate-45 text-[9px] font-bold px-7 py-0.5 tracking-wider bg-[#D4AF37] text-[#0C0C14] ${
                      s.premium ? 'shadow-[0_0_10px_rgba(212,175,55,0.6)]' : ''
                    }`}
                  >
                    {s.badge[language === 'ru' ? 'ru' : 'en']}
                  </span>
                )}
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className={`font-semibold text-white ${s.premium ? 'text-[16px]' : 'text-[15px]'}`}>
                      {s.label[language === 'ru' ? 'ru' : 'en']}
                    </div>
                    <div className="text-[11px] text-[#A1A1AA]/80 mt-0.5">
                      {s.sublabel[language === 'ru' ? 'ru' : 'en']}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {isStars ? (
                      <>
                        <Star className="w-5 h-5 text-[#D4AF37]" style={{ fill: '#D4AF37' }} />
                        <span
                          className="font-mono font-extrabold text-[18px] tabular-nums text-[#D4AF37]"
                          style={{ textShadow: '0 0 12px rgba(212,175,55,0.4)' }}
                        >
                          {fmt(s.cost)}
                        </span>
                      </>
                    ) : (
                      <>
                        <Coins className="w-4 h-4 text-[#D4AF37]" />
                        <span className="font-mono font-bold text-[15px] tabular-nums text-[#D4AF37]">
                          {fmt(s.cost)}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                {!affordable && !isStars && (
                  <div className="text-[10px] text-[#EF4444]/80 mt-1.5">
                    {language === 'ru'
                      ? `Не хватает ${fmt(s.cost - coins)} монет`
                      : `Need ${fmt(s.cost - coins)} more coins`}
                  </div>
                )}
              </button>
            );
          })}
        </div>

        <p className="mt-3 text-[10px] text-[#A1A1AA]/50 text-center leading-snug">
          {language === 'ru'
            ? `${restorerName || 'Jeff'} обходит галерею и поддерживает картины. На время смены — без отдыха.`
            : `${restorerName || 'Jeff'} roams your gallery and tends to paintings. No breaks during a shift.`}
        </p>
      </div>
    </div>
  );
};

export default RestorerHireSheet;
